/**
 * App Utilities
 * Shared helpers for app management across the application
 */

export interface AppMetadata {
  system?: boolean;
  core?: boolean;
  hideFromStore?: boolean;
  defaultSize?: {
    width: number;
    height: number;
    minWidth?: number;
    minHeight?: number;
  };
  [key: string]: any;
}

export interface AppWithMetadata {
  id: string;
  name?: string;
  component_type?: string;
  metadata?: AppMetadata | Record<string, any>;
}

/**
 * Check if an app is a system app (core OS component)
 * System apps cannot be uninstalled, but all except App Store can be removed from dock
 * 
 * @param app - App object with metadata
 * @returns true if the app is marked as a system app
 */
export const isSystemApp = (app?: AppWithMetadata | null): boolean => {
  if (!app || !app.metadata) {
    return false;
  }
  
  const metadata = app.metadata as AppMetadata;
  return metadata.system === true;
};

/**
 * Check if an app is the App Store
 * The App Store is the only app that cannot be removed from the dock
 * 
 * @param app - App object with component_type
 * @returns true if the app is the App Store
 */
export const isAppStoreApp = (app?: AppWithMetadata | null): boolean => {
  if (!app) {
    return false;
  }
  
  return app.component_type === 'AppStoreWindow';
};

/**
 * Check if an app is a core app
 * Core apps are fundamental to the OS (subset of system apps)
 * 
 * @param app - App object with metadata
 * @returns true if the app is marked as a core app
 */
export const isCoreApp = (app?: AppWithMetadata | null): boolean => {
  if (!app || !app.metadata) {
    return false;
  }
  
  const metadata = app.metadata as AppMetadata;
  return metadata.core === true;
};

/**
 * Get default window size from app metadata
 * 
 * @param app - App object with metadata
 * @returns Window size object or null if not specified
 */
export const getAppDefaultSize = (app?: AppWithMetadata | null): AppMetadata['defaultSize'] | null => {
  if (!app || !app.metadata) {
    return null;
  }
  
  const metadata = app.metadata as AppMetadata;
  return metadata.defaultSize || null;
};

/**
 * System app component types (for reference/validation)
 */
export const SYSTEM_APP_COMPONENTS = [
  'AppStoreWindow',
  'RoleManagementWindow',
  'UserRoleManagementWindow',
  'PermissionAuditLogWindow',
  'OrganizationSettingsWindow',
] as const;

export type SystemAppComponent = typeof SYSTEM_APP_COMPONENTS[number];

/**
 * Check if an app should be hidden from the App Store display
 * Apps with metadata.hideFromStore === true will not be shown to users
 * in the App Store, but remain functional in the database
 * 
 * @param app - App object with metadata
 * @returns true if the app should be hidden from the store
 */
export const isHiddenFromStore = (app?: AppWithMetadata | null): boolean => {
  if (!app || !app.metadata) {
    return false;
  }
  
  const metadata = app.metadata as AppMetadata;
  return metadata.hideFromStore === true;
};
